import { create } from 'zustand';

export interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
  isActive?: boolean;
}

export interface Department {
  _id: string;
  name: string;
  description?: string;
  members?: string[];
  isActive?: boolean;
}

interface BusinessStore {
  users: User[];
  departments: Department[];
  isLoadingUsers: boolean;
  isLoadingDepartments: boolean;
  lastUsersFetch: number;
  lastDepartmentsFetch: number;
  
  // Actions
  setUsers: (users: User[]) => void;
  setDepartments: (departments: Department[]) => void;
  setIsLoadingUsers: (loading: boolean) => void;
  setIsLoadingDepartments: (loading: boolean) => void;
  /** Marks a fetch ATTEMPT (success or failure) without replacing the data —
   * used on a failed fetch so shouldRefreshUsers() backs off for the same
   * CACHE_DURATION instead of retrying on every re-render. Without this, a
   * persistently-failing endpoint (e.g. a 500) never advances
   * lastUsersFetch (only setUsers, on the success path, does), so
   * useBusinessData's effect — which re-runs whenever isLoadingUsers flips
   * back to false — sees shouldRefreshUsers() still true and immediately
   * refetches, forever, hammering the backend on every render. */
  markUsersFetchAttempted: () => void;
  markDepartmentsFetchAttempted: () => void;
  updateUser: (userId: string, updates: Partial<User>) => void;
  updateDepartment: (deptId: string, updates: Partial<Department>) => void;
  addUser: (user: User) => void;
  addDepartment: (department: Department) => void;
  removeUser: (userId: string) => void;
  removeDepartment: (deptId: string) => void;
  clearStore: () => void;
  
  // Getters
  getUserById: (userId: string) => User | undefined;
  getDepartmentById: (deptId: string) => Department | undefined;
  shouldRefreshUsers: () => boolean;
  shouldRefreshDepartments: () => boolean;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useBusinessStore = create<BusinessStore>((set, get) => ({
  users: [],
  departments: [],
  isLoadingUsers: false,
  isLoadingDepartments: false,
  lastUsersFetch: 0,
  lastDepartmentsFetch: 0,
  
  setUsers: (users) => set({ users, lastUsersFetch: Date.now() }),
  
  setDepartments: (departments) => set({ departments, lastDepartmentsFetch: Date.now() }),
  
  setIsLoadingUsers: (loading) => set({ isLoadingUsers: loading }),

  setIsLoadingDepartments: (loading) => set({ isLoadingDepartments: loading }),

  markUsersFetchAttempted: () => set({ lastUsersFetch: Date.now() }),

  markDepartmentsFetchAttempted: () => set({ lastDepartmentsFetch: Date.now() }),
  
  updateUser: (userId, updates) => set((state) => ({
    users: state.users.map(user => 
      user._id === userId ? { ...user, ...updates } : user
    )
  })),
  
  updateDepartment: (deptId, updates) => set((state) => ({
    departments: state.departments.map(dept => 
      dept._id === deptId ? { ...dept, ...updates } : dept
    )
  })),
  
  addUser: (user) => set((state) => ({
    users: [...state.users, user]
  })),
  
  addDepartment: (department) => set((state) => ({
    departments: [...state.departments, department]
  })),
  
  removeUser: (userId) => set((state) => ({
    users: state.users.filter(user => user._id !== userId)
  })),
  
  removeDepartment: (deptId) => set((state) => ({
    departments: state.departments.filter(dept => dept._id !== deptId)
  })),
  
  clearStore: () => set({
    users: [],
    departments: [],
    isLoadingUsers: false,
    isLoadingDepartments: false,
    lastUsersFetch: 0,
    lastDepartmentsFetch: 0
  }),
  
  getUserById: (userId) => {
    return get().users.find(user => user._id === userId);
  },
  
  getDepartmentById: (deptId) => {
    return get().departments.find(dept => dept._id === deptId);
  },
  
  shouldRefreshUsers: () => {
    const { lastUsersFetch } = get();
    return Date.now() - lastUsersFetch > CACHE_DURATION;
  },
  
  shouldRefreshDepartments: () => {
    const { lastDepartmentsFetch } = get();
    return Date.now() - lastDepartmentsFetch > CACHE_DURATION;
  }
}));
